import React from 'react';
import { Trans } from '@lingui/react/macro';
import styled from 'styled-components';

import { Box, Display, Flex, Heading, Paragraph } from '@rover/kibble/core';
import MQ from '@rover/kibble/styles/MediaQuery';
import useTheme from '@rover/kibble/themes/useTheme';
import PageLayout from '@rover/react-lib/src/components/layouts/PageLayout';
import { ContentColumn } from '@rover/react-lib/src/components/lists/BulletedList/BulletedListItem';
import sitterDesktop from '@rover/react-lib/src/images/policies/guarantee/rg_lp_photo_03_desktop.jpg';
import sitterMobile from '@rover/react-lib/src/images/policies/guarantee/rg_lp_photo_03_mobile.jpg';
import sitterTablet from '@rover/react-lib/src/images/policies/guarantee/rg_lp_photo_03_tablet.jpg';
import sitterDesktop2 from '@rover/react-lib/src/images/policies/guarantee/rg_lp_photo_04_desktop.jpg';
import sitterMobile2 from '@rover/react-lib/src/images/policies/guarantee/rg_lp_photo_04_mobile.jpg';
import sitterTablet2 from '@rover/react-lib/src/images/policies/guarantee/rg_lp_photo_04_tablet.jpg';
import starsIconLeft from '@rover/react-lib/src/images/policies/guarantee/stars_left_02.svg?url';
import starsIconRight from '@rover/react-lib/src/images/policies/guarantee/stars_right_01.svg?url';
import type { RoverTheme } from '@rover/types/src/RoverTheme';
import inject from '@rover/utilities/injector';

import BulletedList from '../../../../components/lists/BulletedList';
import { Brand2StyledLink } from '../brand2ColorStyles.constants';

import MultiImage, { Coordinates } from './MultiImage';

const { trustAndSafetyContactPhone, trustAndSafetyContactEmail } = inject('Rover.context', {});
const ResponsivePageLayout = styled(PageLayout)`
  margin-bottom: ${({ theme }) => theme.space['24x']};
  ${MQ.XS.toString()} {
    margin-bottom: ${({ theme }) => theme.space['16x']};
  }
  margin-top: ${({ theme }) => theme.space['0x']};
`;
const StyledBulletedList = styled(BulletedList)`
  ${ContentColumn} {
    margin-left: ${({ theme }) => theme.space['6x']};
  }
`;
const defaultCoordinates: Coordinates = {
  height: '200px',
  width: '200px',
};
const tabletCoordinates: Coordinates = {
  height: '150px',
  width: '150px',
};
const mobileCoordinates: Coordinates = {
  height: '150px',
  width: '150px',
};
const mailToHref = `mailto:${trustAndSafetyContactEmail}`;
const getListOfStepsBeforeClaim = (colors: RoverTheme['colors']) =>
  [
    {
      label: '1.',
      title: (
        <Heading size="300" textColor="primary">
          <Trans>Notify our support team</Trans>
        </Heading>
      ),
      content: (
        <>
          <Paragraph textColor="primary" webProps={{ maxWidth: 'none' }}>
            <Trans comment="{trustAndSafetyContactPhone} this is the Trust and Safety contact phone number; {trustAndSafetyContactEmail} this is the Trust and Safety contact email address; {emergencyHotlineHours} is '24/7' for en-us, en-ca, fr-ca and empty string for other languages">
              Call {trustAndSafetyContactPhone} or email {trustAndSafetyContactEmail} and notify our{' '}
              Trust & Safety team of the incident if you have not already. We can’t help if we don’t
              know what has happened!
            </Trans>
          </Paragraph>
          <Paragraph mb="0x" textColor="primary" webProps={{ maxWidth: 'none' }}>
            <Trans>
              In many cases, we can help you take immediate steps to mitigate the injury or damage
              caused by an incident, such as connecting you with a board-certified veterinarian by
              phone or providing resources to help locate a lost dog. For this reason, we ask both
              pet care providers and pet owners to notify us as soon as possible after an incident
              occurs—and in any event no later than 48 hours after the booking has ended.
            </Trans>
          </Paragraph>
        </>
      ),
      hasBorder: false,
      color: colors.green['700'],
    },
    {
      label: '2.',
      title: (
        <Heading size="300" textColor="primary">
          <Trans>Contact the other party</Trans>
        </Heading>
      ),
      content: (
        <Paragraph mb="0x" textColor="primary" webProps={{ maxWidth: 'none' }}>
          <Trans>
            Contact the other party through your app or web inbox to see if you can find a
            resolution together. We know from experience that issues are often best resolved by
            simply reaching out to the other person. It’s important to communicate through the
            platform so there is a record of the conversation. If you're unable to come to an
            agreement, absolutely let us know.
          </Trans>
        </Paragraph>
      ),
      hasBorder: false,
      color: colors.green['700'],
    },
    {
      label: '3.',
      title: (
        <Heading size="300" textColor="primary">
          <Trans>Work with your insurance provider</Trans>
        </Heading>
      ),
      content: (
        <Paragraph mb="0x" textColor="primary" webProps={{ maxWidth: 'none' }}>
          <Trans>
            If you have insurance that may cover some or all costs arising from an injury or damage
            otherwise falling under the Rover Guarantee, such as pet or homeowner’s insurance, you
            should file a claim under that policy before seeking reimbursement from Rover. In such a
            case the Rover Guarantee may be leveraged to meet deductibles or cover costs that fall
            outside your policy.
          </Trans>
        </Paragraph>
      ),
      hasBorder: false,
      color: colors.green['700'],
    },
  ].map((item, index) => ({ ...item, key: index, hasBorder: false }));
const getListOfHowToFileAClaim = (colors: RoverTheme['colors']) =>
  [
    {
      label: '1.',
      title: (
        <Heading size="300" textColor="primary">
          <Trans>First, determine if you’re eligible</Trans>
        </Heading>
      ),
      content: (
        <Paragraph textColor="primary" webProps={{ maxWidth: 'none' }} mb="0x">
          <Trans>
            To be eligible, the pet care service related to the reimbursable incident must have been
            booked and paid for on the platform, and the injury or damage must have occurred during
            the booked dates. You must also have notified us of the incident within 48 hours of the
            end of the service.
          </Trans>
        </Paragraph>
      ),
      hasBorder: false,
      color: colors.green['700'],
    },
    {
      label: '2.',
      title: (
        <Heading size="300" textColor="primary">
          <Trans>To file your claim</Trans>
        </Heading>
      ),
      content: (
        <Paragraph textColor="primary" webProps={{ maxWidth: 'none' }} mb="0x">
          <Trans comment="{trustAndSafetyContactPhone} this is the Trust and Safety contact phone number; {trustAndSafetyContactEmail} this is the Trust and Safety contact email address;">
            Contact our Trust & Safety team at {trustAndSafetyContactPhone} or{' '}
            <Brand2StyledLink href={mailToHref}>{trustAndSafetyContactEmail}</Brand2StyledLink> and
            we'll guide you through the claims process.
          </Trans>
        </Paragraph>
      ),
      hasBorder: false,
      color: colors.green['700'],
    },
    {
      label: '3.',
      title: (
        <Heading size="300" textColor="primary">
          <Trans>Next steps after filing</Trans>
        </Heading>
      ),
      content: (
        <>
          <Paragraph textColor="primary" webProps={{ maxWidth: 'none' }}>
            <Trans>
              All claim-related information will be evaluated to determine if the incident is the
              result of care provided during a service booked and paid for on the platform.
            </Trans>
          </Paragraph>
          <Paragraph textColor="primary" webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              We'll reach out to everyone involved to gather as much information as possible,
              including a written statement from each party. These documents will play a key role in
              the review process.
            </Trans>
          </Paragraph>
        </>
      ),
      hasBorder: false,
      color: colors.green['700'],
    },
  ].map((item, index) => ({ ...item, key: index, hasBorder: false }));

const FileAClaim = (): JSX.Element => {
  const { colors } = useTheme();
  const listOfStepsBeforeClaim = getListOfStepsBeforeClaim(colors);
  const listOfHowToFileAClaim = getListOfHowToFileAClaim(colors);

  return (
    <Flex justifyContent="center" data-testid="file-a-claim">
      <ResponsivePageLayout>
        <Flex flexWrap="wrap-reverse" gap="12x" mb="12x">
          <Box flex="1 0 40%">
            <Display id="file-a-claim" size="500" mb="6x">
              <Trans>Take these steps before filing a claim</Trans>
            </Display>
            <StyledBulletedList items={listOfStepsBeforeClaim} />
          </Box>
          <Box>
            <MultiImage
              defaultImageSrc={sitterDesktop}
              tabletImageSrc={sitterTablet}
              mobileImageSrc={sitterMobile}
              additionalImage={starsIconRight}
              defaultCoordinates={defaultCoordinates}
              tabletCoordinates={tabletCoordinates}
              mobileCoordinates={mobileCoordinates}
              right
            />
          </Box>
        </Flex>
        <Flex flexWrap="wrap" gap="12x">
          <Box>
            <MultiImage
              defaultImageSrc={sitterDesktop2}
              tabletImageSrc={sitterTablet2}
              mobileImageSrc={sitterMobile2}
              additionalImage={starsIconLeft}
              defaultCoordinates={defaultCoordinates}
              tabletCoordinates={tabletCoordinates}
              mobileCoordinates={mobileCoordinates}
            />
          </Box>

          <Box flex="1 0 40%">
            <Display size="500" mb="6x" webProps={{ as: 'h2' }}>
              <Trans>How to file a claim</Trans>
            </Display>
            <Paragraph textColor="primary" webProps={{ maxWidth: 'none' }}>
              <Trans>
                If none of the above steps resolve the issue, you can file a claim for reimbursement
                under the Rover Guarantee.
              </Trans>
            </Paragraph>
            <StyledBulletedList items={listOfHowToFileAClaim} />
          </Box>
        </Flex>
      </ResponsivePageLayout>
    </Flex>
  );
};

export default FileAClaim;
